import { useEffect, useState, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  BellIcon, 
  CalendarIcon, 
  WrenchScrewdriverIcon, 
  SparklesIcon, 
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { announcementAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import useAdminTableState from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'
import AnnouncementTableRow from './AnnouncementTableRow'
import AnnouncementBulkActions from './AnnouncementBulkActions'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AdminAnnouncementsSection')

const AdminAnnouncementsSection = memo(function AdminAnnouncementsSection({ onBack, onStatsUpdate }) {
  const navigate = useNavigate()
  const { execute } = useAsyncOperation()
  const [selectedIds, setSelectedIds] = useState(new Set())

  const {
    data: announcements,
    loading,
    error,
    searchInput,
    search,
    filter,
    pagination,
    handleSearchChange,
    handleFilterChange,
    handlePageChange,
    refetch
  } = useAdminTableState(
    async (params) => {
      return execute(async () => {
        // Hook provides params with: { page, limit, search?, filter? }
        const apiParams = { ...params }
        
        // Map filter to status
        if (params.filter && params.filter !== 'all') {
          apiParams.status = params.filter
          delete apiParams.filter
        }
        
        const response = await announcementAPI.getAllAnnouncements(apiParams)
        const announcementsList = response.data?.data || []
        
        return {
          data: announcementsList,
          pagination: {
            page: response.data?.page || 1,
            totalPages: response.data?.totalPages || 1,
            total: response.data?.total || 0
          }
        }
      })
    },
    { pageSize: 20 }
  )

  const getAnnouncementTypeIcon = (type) => {
    switch (type) {
      case 'update':
        return BellIcon
      case 'event':
        return CalendarIcon
      case 'maintenance':
        return WrenchScrewdriverIcon
      case 'feature':
        return SparklesIcon
      default:
        return InformationCircleIcon
    }
  }

  const getAnnouncementTypeColor = (type) => {
    switch (type) {
      case 'update':
        return 'bg-blue-50 text-blue-700 border-blue-200'
      case 'event':
        return 'bg-purple-50 text-purple-700 border-purple-200'
      case 'maintenance':
        return 'bg-gray-50 text-gray-700 border-gray-200'
      case 'feature':
        return 'bg-green-50 text-green-700 border-green-200'
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200'
    }
  }

  const togglePublish = useCallback(async (announcementId, isPublished) => {
    try {
      if (isPublished) {
        await announcementAPI.unpublishAnnouncement(announcementId)
      } else {
        await announcementAPI.publishAnnouncement(announcementId)
      }
      refetch()
      if (onStatsUpdate) {
        onStatsUpdate()
      }
    } catch (error) {
      logger.error('Failed to toggle publish', error)
    }
  }, [refetch, onStatsUpdate])

  const togglePin = useCallback(async (announcementId, isPinned) => {
    try {
      await announcementAPI.togglePinAnnouncement(announcementId, !isPinned)
      refetch()
      if (onStatsUpdate) {
        onStatsUpdate()
      }
    } catch (error) {
      logger.error('Failed to toggle pin', error)
    }
  }, [refetch, onStatsUpdate])

  const deleteAnnouncement = useCallback(async (announcementId) => {
    if (confirm('Are you sure you want to delete this announcement?')) {
      try {
        await announcementAPI.deleteAnnouncement(announcementId)
        refetch()
        if (onStatsUpdate) {
          onStatsUpdate()
        }
      } catch (error) {
        logger.error('Failed to delete announcement', error)
      }
    }
  }, [refetch, onStatsUpdate])

  const filterOptions = [
    { value: 'all', label: 'All Announcements' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Drafts' }
  ]

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(announcements.map(a => a.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [announcements])

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Are you sure you want to delete ${selectedIds.size} announcement(s)?`)) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => announcementAPI.deleteAnnouncement(id)))
      setSelectedIds(new Set())
      refetch()
      if (onStatsUpdate) onStatsUpdate()
    }, { successMessage: `${selectedIds.size} announcement(s) deleted successfully` })
  }, [selectedIds, refetch, onStatsUpdate, execute])

  const handleBulkPublish = useCallback(async () => {
    if (selectedIds.size === 0) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => announcementAPI.publishAnnouncement(id)))
      setSelectedIds(new Set())
      refetch()
      if (onStatsUpdate) onStatsUpdate()
    }, { successMessage: `${selectedIds.size} announcement(s) published successfully` })
  }, [selectedIds, refetch, onStatsUpdate, execute])

  const handleBulkUnpublish = useCallback(async () => {
    if (selectedIds.size === 0) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => announcementAPI.unpublishAnnouncement(id)))
      setSelectedIds(new Set())
      refetch()
      if (onStatsUpdate) onStatsUpdate()
    }, { successMessage: `${selectedIds.size} announcement(s) unpublished successfully` })
  }, [selectedIds, refetch, onStatsUpdate, execute])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Announcements Management</h2>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin/announcements/new')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 text-sm font-medium"
          >
            + Create Announcement
          </button>
          <button
            onClick={onBack}
            className="text-sm text-primary hover:text-primary-600"
          >
            ← Back to Overview
          </button>
        </div>
      </div>

      <AdminTableControls
        searchValue={searchInput}
        searchPlaceholder="Search by title or content..."
        onSearchChange={handleSearchChange}
        filterValue={filter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemLabel="announcements"
        onPageChange={handlePageChange}
      />

      {/* Bulk Actions Bar */}
      <AnnouncementBulkActions
        selectedCount={selectedIds.size}
        onPublish={handleBulkPublish}
        onUnpublish={handleBulkUnpublish}
        onDelete={handleBulkDelete}
        onClear={() => setSelectedIds(new Set())}
      />

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 lg:px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={announcements.length > 0 && selectedIds.size === announcements.length}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Published
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-4 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-red-500">{error}</td>
              </tr>
            ) : announcements.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-4 text-center text-gray-500">
                  No announcements found
                </td>
              </tr>
            ) : (
              announcements.map((announcement) => (
                <AnnouncementTableRow
                  key={announcement.id}
                  announcement={announcement}
                  isSelected={selectedIds.has(announcement.id)}
                  onSelect={handleSelectOne}
                  onTogglePublish={togglePublish}
                  onTogglePin={togglePin}
                  onDelete={deleteAnnouncement}
                  getTypeIcon={getAnnouncementTypeIcon}
                  getTypeColor={getAnnouncementTypeColor}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
})

export default AdminAnnouncementsSection
